import React from 'react';
import { Card, Badge, Button, StatusBadge } from '@/components/ui';
import { CardList } from '@/components/ui/CardList';
import { getRoleBadgeVariant, getRoleIcon } from '@/utils/role';
import type { AdminUser } from '../types';

interface AdminUsersMobileListProps {
  users: AdminUser[];
  onToggleLock: (id: string, isActive: boolean) => void;
  onViewDetail: (id: string) => void;
  onEdit: (user: AdminUser) => void;
  onImpersonate: (user: AdminUser) => void;
}

export function AdminUsersMobileList({ users, onToggleLock, onViewDetail, onEdit, onImpersonate }: AdminUsersMobileListProps) {
  return (
    <div className="md:hidden">
      <CardList>
        {users.map((user) => {
          const isPersonal = !user.company_id || !user.company_name || user.company_name.toLowerCase().includes('cá nhân');
          return (
          <Card
            key={user.id}
            onClick={() => onViewDetail(user.id)}
            className="p-4 flex flex-col gap-4 bg-white border-none shadow-sm relative overflow-hidden group"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 font-medium border border-slate-100">
                  {user.avatar_path ? (
                    <img src={user.avatar_path} alt="" className="w-full h-full rounded-xl object-cover" />
                  ) : (
                    user.full_name.charAt(0)
                  )}
                </div>
                <div>
                  <h4 className="font-medium sm:text-sm text-xs text-slate-900 leading-none mb-1">{user.full_name}</h4>
                  <p className="text-xs text-slate-400 font-medium">{user.email}</p>
                </div>
              </div>
              {user.is_active ? (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span className="text-[11px] sm:text-xs font-medium text-emerald-700">Hoạt động</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200">
                  <span className="relative flex h-2 w-2">
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-400"></span>
                  </span>
                  <span className="text-[11px] sm:text-xs font-medium text-slate-600">Đã khóa</span>
                </div>
              )}
            </div>

            <div className={`flex items-center justify-between p-3 rounded-xl border ${isPersonal ? 'bg-slate-50 border-slate-100' : 'bg-brand-50/30 border-brand-100'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold border ${isPersonal ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-white text-brand-600 border-brand-100 shadow-sm'}`}>
                  {user.company_code ? user.company_code.charAt(0) : '👤'}
                </div>
                <span className={`text-xs font-bold ${isPersonal ? 'text-slate-500' : 'text-brand-700'}`}>
                  {user.company_name || 'Cá nhân'}
                </span>
              </div>
              <Badge variant={getRoleBadgeVariant(user.role_code)} className="text-xs font-medium px-2 flex items-center gap-1">
                <i className={getRoleIcon(user.role_code)} />
                {user.role_name || user.role_code}
              </Badge>
            </div>

            <div className="flex items-center justify-end gap-1 pt-2 border-t border-slate-50">
              <Button
                variant="ghost"
                size="sm"
                className="w-8 h-8 p-0 text-slate-400"
                onClick={(e) => { e.stopPropagation(); onViewDetail(user.id); }}
              >
                <i className="ri-eye-line" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={user.is_active ? "text-slate-400" : "text-emerald-600"}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleLock(user.id, !user.is_active);
                }}
              >
                <i className={user.is_active ? "ri-lock-2-line" : "ri-lock-unlock-line"} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-8 h-8 p-0 text-blue-400"
                onClick={(e) => { e.stopPropagation(); onEdit(user); }}
              >
                <i className="ri-edit-line" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-8 h-8 p-0 text-amber-400"
                onClick={(e) => { e.stopPropagation(); onImpersonate(user); }}
              >
                <i className="ri-user-shared-line" />
              </Button>
            </div>
          </Card>
          );
        })}
      </CardList>
    </div>
  );
}
